// 計測リンク成績の一覧（全エントリー横断）。旧SPA app.js renderLinks の移植。
// 発行・削除はエントリー詳細の「計測リンク」で行う（ここは見る・コピー・該当エントリーを開くだけ）。
import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchGfLinks } from "../../../api/giftingClient";
import { fmtYmd } from "../../../lib/giftingRules";
import type { GfData, GfLink, GfLinkDestination, GfNav, Loadable } from "../../../types/gifting";
import { errMsg } from "../gfUtil";
import { GfButton, GfErrorBanner, GfModal, GfSkeleton, copyText } from "../ui/GfUi";

const DEST_LABEL: Record<GfLinkDestination, string> = { shopify: "Shopify", rakuten: "楽天市場", amazon: "Amazon", other: "その他" };

/** 上流は influencer_name / product_name も返す（契約の GfLink には無い任意項目） */
type LinkRow = GfLink & { influencer_name?: string | null; product_name?: string | null };

export function LinksModal({
  open,
  onClose,
  data,
  nav,
  toast,
}: {
  open: boolean;
  onClose: () => void;
  data: GfData;
  nav: GfNav;
  toast: (m: string) => void;
}) {
  const [list, setList] = useState<Loadable<LinkRow[]>>({ status: "loading" });

  const load = useCallback(async () => {
    try {
      setList({ status: "ok", data: (await fetchGfLinks()) as LinkRow[] });
    } catch (e) {
      setList({ status: "error", message: errMsg(e) });
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    setList({ status: "loading" });
    void load();
  }, [open, load]);

  const creatorName = useMemo(() => new Map(data.board.creators.map((c) => [c.id, c.name])), [data.board.creators]);
  const entryIds = useMemo(() => new Set(data.board.entries.map((e) => e.id)), [data.board.entries]);

  const rows = list.status === "ok" ? [...list.data].sort((a, b) => b.clicks_7d - a.clicks_7d || b.clicks_total - a.clicks_total) : [];
  const total = rows.reduce((s, l) => s + l.clicks_total, 0);
  const total7 = rows.reduce((s, l) => s + l.clicks_7d, 0);

  const openEntry = (dealId: string) => {
    onClose();
    nav.openEntry(dealId, "deals");
  };

  return (
    <GfModal
      open={open}
      title="計測リンクの成績"
      width={720}
      onClose={onClose}
      footer={
        <>
          <GfButton onClick={() => void load()} disabled={list.status === "loading"}>
            更新
          </GfButton>
          <GfButton onClick={onClose}>閉じる</GfButton>
        </>
      }
    >
      <div className="gf-note">クリック数（計／直近7日）。発行・削除はエントリー詳細の「計測リンク」から行います。</div>
      {list.status === "loading" && <GfSkeleton height={160} />}
      {list.status === "error" && <GfErrorBanner message={`計測リンクを読み込めませんでした。${list.message}`} onRetry={() => void load()} />}
      {list.status === "ok" && rows.length === 0 && <div className="gf-note">まだ計測リンクがありません。</div>}
      {rows.length > 0 && (
        <>
          <div className="gf-links-total">
            <span>
              {rows.length}本・計 <span className="gf-mono">{total.toLocaleString()}</span>
            </span>
            <span>
              7日 <span className="gf-mono">{total7.toLocaleString()}</span>
            </span>
          </div>
          <div className="gf-links-list">
            {rows.map((l) => {
              const who = (l.influencer_id && creatorName.get(l.influencer_id)) || l.influencer_name || "—";
              return (
                <div className="gf-links-row" key={l.id}>
                  <div className="gf-links-main">
                    <span className="gf-links-title">
                      {who}・{DEST_LABEL[l.destination] ?? l.destination}
                      {l.label ? `・${l.label}` : ""}
                      {l.is_active === 0 && <span className="gf-ig-linked">停止中</span>}
                    </span>
                    <span className="gf-links-url gf-mono">{l.short_url}</span>
                    <span className="gf-links-dest">→ {l.dest_url}</span>
                    <span className="gf-note">
                      {fmtYmd(l.created_at)} 発行{l.coupon_code ? `・クーポン ${l.coupon_code}` : ""}
                    </span>
                  </div>
                  <div className="gf-links-side">
                    <span className="gf-links-clicks">
                      <span className="gf-mono">{l.clicks_total.toLocaleString()}</span>
                      <span className="gf-faint">計</span>
                      <span className="gf-mono">{l.clicks_7d.toLocaleString()}</span>
                      <span className="gf-faint">7日</span>
                    </span>
                    <span className="gf-inline-row is-tight">
                      <button
                        type="button"
                        className="gf-link-btn"
                        onClick={() => void copyText(l.short_url).then((ok) => toast(ok ? "リンクをコピーしました" : "コピーできませんでした"))}
                      >
                        コピー
                      </button>
                      {l.deal_id && entryIds.has(l.deal_id) && (
                        <button type="button" className="gf-link-btn" onClick={() => openEntry(l.deal_id as string)}>
                          エントリー
                        </button>
                      )}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </GfModal>
  );
}
